import { isEqual } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';

import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { hasPermission } from 'src/utils/has-permissions';

import { useAuthContext } from 'src/auth/hooks';
import { AuthPermissions } from 'src/auth/permissions';
import { useAppDispatcher, useAppSelector } from 'src/redux/store';
import {
  deleteStationeryReturn,
  getStationeryReturns,
  removeStationeryReturn,
} from 'src/redux/slices/stationery-return';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
import TableErrorData from 'src/components/table/table-error-data';
import {
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  TableSkeleton,
  useTable,
} from 'src/components/table';

import {
  IStationeryReturnListItem,
  IStationeryReturnTableFilters,
  IStationeryReturnTableFilterValue,
  IStationeryReturnTableSort,
} from 'src/types/stationery-return';

import StationeryReturnTableRow from '../stationery-return-table-row';
import StationeryReturnTableToolbar from '../stationery-return-table-toolbar';

const TABLE_HEAD = [
  { id: 'returnCode', label: 'Return #', align: 'left' },
  { id: 'receivedBy', label: 'Received By', align: 'left' },
  { id: 'returnDate', label: 'Return Date', align: 'left' },
  { id: '' },
];

const defaultFilters: IStationeryReturnTableFilters = {
  generalSearch: '',
  startDate: null,
  endDate: null,
};

// Sorted by createdAt (not updatedAt) so the top row on an unfiltered first
// page always matches the backend's "most recently created" rule — that's
// the only row edit/delete is enabled for.
const defaultSortBy: IStationeryReturnTableSort = {
  sortBy: 'createdAt',
  order: 'desc',
};

export default function StationeryReturnListView() {
  const { status, returns, error } = useAppSelector((state) => state.stationeryReturn);

  const { user: authUser } = useAuthContext();
  const table = useTable({
    defaultRowsPerPage: 25,
  });

  const dispatch = useAppDispatcher();

  const { page, rowsPerPage } = table;

  const settings = useSettingsContext();

  const denseHeight = table.dense ? 60 : 80;

  const router = useRouter();

  const [filters, setFilters] = useState(defaultFilters);

  const [sort, setSort] = useState(defaultSortBy);

  const canReset = !isEqual(defaultFilters, filters);

  const canDetermineLastReturn =
    page === 0 &&
    sort.sortBy === 'createdAt' &&
    sort.order === 'desc' &&
    !filters.generalSearch &&
    !filters.startDate &&
    !filters.endDate;

  // Admins can edit/delete any return, not just the most recently created
  // one — mirrors the backend's assertIsLastReturn() bypass.
  const isAdmin = hasPermission(
    authUser,
    'any',
    AuthPermissions.ADMIN_PERMISSION,
    AuthPermissions.SUPER_ADMIN_PERMISSION
  );

  const handleFilters = useCallback(
    (name: string, value: IStationeryReturnTableFilterValue) => {
      table.onResetPage();

      setFilters((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    },
    [table]
  );

  const handleEditRow = useCallback(
    (id: string) => {
      router.push(paths.dashboard.stationeryReturn.edit(id));
    },
    [router]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const fetchPaginatedReturns = useCallback(() => {
    dispatch(
      getStationeryReturns({
        page,
        limit: rowsPerPage,
        generalSearch: filters.generalSearch,
        startDate: filters.startDate,
        endDate: filters.endDate,
        sort: sort.order,
        sortBy: sort.sortBy,
      })
    );
  }, [dispatch, page, rowsPerPage, sort.order, sort.sortBy, filters]);

  const handleDeleteRow = useCallback(
    async (item: IStationeryReturnListItem, index: number) => {
      dispatch(removeStationeryReturn({ returnId: item._id }));

      try {
        await dispatch(deleteStationeryReturn({ item, index })).unwrap();

        fetchPaginatedReturns();
        enqueueSnackbar('Return deleted');
      } catch (e) {
        if (typeof e === 'string') {
          enqueueSnackbar(e, {
            variant: 'error',
          });
        } else {
          enqueueSnackbar(e?.message, {
            variant: 'error',
          });
        }
      }
    },
    [dispatch, fetchPaginatedReturns]
  );

  useEffect(() => {
    fetchPaginatedReturns();
  }, [fetchPaginatedReturns]);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Stationery Returns"
        links={[]}
        action={
          <>
            {hasPermission(
              authUser,
              'any',
              AuthPermissions.CREATE_STATIONERY_RETURN,
              AuthPermissions.ADMIN_PERMISSION,
              AuthPermissions.SUPER_ADMIN_PERMISSION
            ) && (
              <Button
                component={RouterLink}
                href={paths.dashboard.stationeryReturn.new}
                variant="contained"
                startIcon={<Iconify icon="mingcute:add-line" />}
              >
                New Return
              </Button>
            )}
          </>
        }
        sx={{
          mb: { xs: 3 },
        }}
      />

      <Card>
        <StationeryReturnTableToolbar
          filters={filters}
          onFilters={handleFilters}
          handleResetFilters={handleResetFilters}
          canReset={canReset}
        />

        <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '450px' }}>
          <Scrollbar>
            <Table size={table.dense ? 'small' : 'medium'} sx={{ minWidth: 960 }}>
              <TableHeadCustom
                order={sort.order}
                orderBy={sort.sortBy}
                headLabel={TABLE_HEAD}
                rowCount={returns.items.length}
                numSelected={table.selected.length}
                onSort={(id) => {
                  if (id === sort.sortBy) {
                    setSort((prev) => ({
                      ...prev,
                      order: prev.order === 'asc' ? 'desc' : 'asc',
                    }));
                  } else {
                    setSort({
                      sortBy: id,
                      order: 'asc',
                    });
                  }
                }}
              />

              <TableBody>
                {status === 'loading' ? (
                  [...Array(table.rowsPerPage)].map((i, index) => (
                    <TableSkeleton key={index} sx={{ height: denseHeight }} />
                  ))
                ) : (
                  <>
                    {returns?.items.map((row: IStationeryReturnListItem, index: number) => (
                      <StationeryReturnTableRow
                        key={row._id}
                        row={row}
                        isEditable={isAdmin || (canDetermineLastReturn && index === 0)}
                        onDeleteRow={() => handleDeleteRow(row, index)}
                        onEditRow={() => handleEditRow(row._id)}
                      />
                    ))}
                  </>
                )}

                {status !== 'failure' && returns.count === 0 && (
                  <TableNoData message="No returns found" notFound={returns.count === 0} />
                )}

                {status === 'failure' && <TableErrorData notFound error={error} />}
              </TableBody>
            </Table>
          </Scrollbar>
        </TableContainer>

        <TablePaginationCustom
          count={returns.count}
          page={table.page}
          rowsPerPage={table.rowsPerPage}
          onPageChange={table.onChangePage}
          onRowsPerPageChange={table.onChangeRowsPerPage}
          dense={table.dense}
          onChangeDense={table.onChangeDense}
        />
      </Card>
    </Container>
  );
}
